function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { css, StyleSheet } from 'aphrodite';
import compose from 'recompose/compose';
import withState from 'recompose/withState';
import withHandlers from 'recompose/withHandlers';
import withProps from 'recompose/withProps';
import { STANDARD_TEXT, TSS_DARK_BLUE, TSS_BLUE, TSS_BLUE_1, TSS_BLUE_2, TSS_GREEN, stylePropType, childrenPropType, escapeRegExp, getScrollParent } from 'tss-core-lib-common';
import Icon from 'react-svg-ikon';

import clickOutsideEnhancer from './clickOutsideEnhancer';
import withRefs from './withRefsEnhancer';
import { IMG_ICON_CHEVRON_DOWN_SMALL, IMG_ICON_CHECKMARK } from '../img';
import Checkbox from './Checkbox';
import Spinner from './Spinner';
import SearchInput from './SearchInput';

var styles = void 0;

var dropdownValueProps = [PropTypes.string, PropTypes.number, PropTypes.bool];

var enhanceDropdownItem = withHandlers({
  onClick: function onClick(props) {
    return function (event) {
      event.stopPropagation();
      props.onClick(props.value);
    };
  }
});

var DropdownItem = function DropdownItem(_ref) {
  var children = _ref.children,
      value = _ref.value,
      label = _ref.label,
      selected = _ref.selected,
      marked = _ref.marked,
      onClick = _ref.onClick,
      id = _ref.id,
      identifier = _ref.identifier;
  return React.createElement(
    'div',
    {
      value: value || 'no value found',
      'data-id': 'drop-down-item-' + id,
      identifier: identifier,
      className: css(styles.listItem, marked && styles.markedListItem, selected && styles.selectedListItem),
      tabIndex: 0,
      role: 'button',
      onClick: onClick
    },
    React.createElement(
      'span',
      { className: css(STANDARD_TEXT, styles.label) },
      label || value || ''
    ),
    children
  );
};

DropdownItem.propTypes = {
  children: childrenPropType,
  value: PropTypes.oneOfType([].concat(dropdownValueProps, [PropTypes.shape({
    format: PropTypes.string,
    pagination: PropTypes.bool
  })])),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  identifier: PropTypes.string,
  selected: PropTypes.bool,
  marked: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

DropdownItem.defaultProps = {
  children: null,
  label: null,
  identifier: null,
  id: null,
  value: null,
  selected: false,
  marked: false
};

var defaultSorter = function defaultSorter(a, b) {
  if (a.label && typeof b.label === 'undefined') {
    return 1;
  }
  if (b.label && typeof a.label === 'undefined') {
    return -1;
  }
  return a.label.localeCompare(b.label);
};

var close = function close(props) {
  props.setIsOpen(false);
  props.setMarkedValue(null);
  props.setSearchValue('');
  props.setFilteredItems(null);
};

var valueSelector = function valueSelector(props) {
  return function (value) {
    if (props.multiple) {
      var values = [].concat(_toConsumableArray(props.value));
      var index = values.indexOf(value);
      if (index >= 0) {
        values.splice(index, 1);
      } else {
        values.push(value);
      }
      props.onChange(values);
    } else {
      var item = props.items.find(function (_ref2) {
        var v = _ref2.value;
        return v === value;
      });
      props.setCachedItem(item);
      if (props.allowReSelect !== false) {
        if (props.onChange) {
          props.onChange(value);
        }
      } else {
        var sameSelect = props.value === value;
        if (!sameSelect) {
          if (props.onChange) {
            props.onChange(value);
          }
        }
      }
      close(props);
    }
  };
};

var enhanceDropdown = compose(withState('isOpen', 'setIsOpen', false), withState('timer', 'setTimer', null), withState('filteredItems', 'setFilteredItems', null), withState('searchValue', 'setSearchValue', ''), withState('markedValue', 'setMarkedValue', null), withState('cachedItem', 'setCachedItem', null), withState('above', 'setAbove', function (props) {
  return Boolean(props.above);
}), withProps(function (props) {
  var extraProps = {};
  if (props.multiple && !Array.isArray(props.value)) {
    extraProps.value = props.value === undefined ? [] : [props.value];
  }

  if (props.sorter !== null) {
    // TODO Could be a taxing operation.
    extraProps.items = [].concat(_toConsumableArray(props.items)).sort(props.sorter || defaultSorter);
  }
  return extraProps;
}), withHandlers({
  open: function open(props) {
    return function () {
      var listContainer = props.getRef('listContainer');

      var _listContainer$getBou = listContainer.getBoundingClientRect(),
          bottom = _listContainer$getBou.bottom;

      var scrollParent = getScrollParent(listContainer);
      var height = window.innerHeight;
      if (scrollParent) {
        height = scrollParent.getBoundingClientRect().bottom;
      }
      props.setAbove(bottom + 150 > height);
      setTimeout(function () {
        return props.setIsOpen(true);
      }, 0);

      setTimeout(function () {
        var input = props.getRef('searchInput');
        if (input) {
          input.focus();
        }
      }, 10);
    };
  }
}), withHandlers({
  onContainerKeyUp: function onContainerKeyUp(props) {
    return function (event) {
      if (event.which === 9 && !props.isOpen) {
        props.open();
      }
    };
  },

  onClickOutside: function onClickOutside(props) {
    return function (event) {
      var container = props.getRef('container');
      if (props.isOpen && container && event.target !== container && !container.contains(event.target)) {
        setTimeout(function () {
          return close(props);
        }, 50);
      }
    };
  },

  onContainerClick: function onContainerClick(props) {
    return function (event) {
      var listContainer = props.getRef('listContainer');
      if (!listContainer.contains(event.target)) {
        if (props.isOpen) {
          close(props);
        } else {
          props.open();
        }
      }
    };
  },

  onContainerKeyDown: function onContainerKeyDown(props) {
    return function (event) {
      if (event.keyCode === 27) {
        // ESC
        close(props);
      } else if (event.keyCode === 38 || event.keyCode === 40) {
        // Up / Down
        var items = props.filteredItems || props.items;
        var index = items.findIndex(function (_ref3) {
          var value = _ref3.value;
          return value === props.markedValue;
        });
        if (event.keyCode === 38) {
          index = (index + items.length - 1) % items.length;
        } else {
          index = (index + items.length + 1) % items.length;
        }
        var newValue = items[index].value;
        props.setMarkedValue(newValue);
        event.preventDefault();
      } else if (event.keyCode === 13) {
        // Enter
        var _items = props.filteredItems || props.items;

        if (props.markedValue) {
          valueSelector(props)(props.markedValue);
        } else if (props.typeahead !== false && _items.length === 1) {
          valueSelector(props)(_items[0].value);
        }
      }
    };
  },

  onItemClick: valueSelector,

  onSearchChange: function onSearchChange(props) {
    return function (searchValue) {
      props.setSearchValue(searchValue);

      if (props.backEndFilteringEnabled) {
        props.fetchNewItems(searchValue);
        return;
      }
      if (props.onSearch) {
        // If onSearch is supplied, defer searching and sorting to the parent component
        props.onSearch(searchValue);
      } else if (searchValue === '') {
        props.setFilteredItems(null);
      } else {
        var re = new RegExp(escapeRegExp(searchValue), 'i');
        props.setFilteredItems(props.items.filter(function (_ref4) {
          var label = _ref4.label;
          return label.match(re);
        }));
      }
    };
  },

  onSelectAll: function onSelectAll(props) {
    return function (selectAll) {
      return props.onChange(selectAll ? (props.filteredItems || props.items).map(function (_ref5) {
        var value = _ref5.value;
        return value;
      }) : []);
    };
  }
}));

function getSelectedLabel(selectedValue, items, intl) {
  var defaultLabel = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';

  if (Array.isArray(selectedValue)) {
    if (selectedValue.length === 0) {
      return defaultLabel;
    }
    if (selectedValue.length > 1) {
      return intl.formatMessage({ id: 'module.components.dropdown.x.selected' }, { count: selectedValue.length });
    }

    var _item = items.find(function (_ref6) {
      var value = _ref6.value;
      return value === selectedValue[0] && value;
    });
    return _item ? _item.label : defaultLabel;
  }
  var item = items.find(function (_ref7) {
    var value = _ref7.value;
    return value === selectedValue && value;
  });
  return item ? item.label : defaultLabel;
}

function isValueSelected(itemValue, propValue) {
  if (Array.isArray(propValue)) {
    return propValue.indexOf(itemValue) >= 0;
  }
  return Boolean(itemValue === propValue);
}

var Dropdown = function Dropdown(_ref8) {
  var intl = _ref8.intl,
      id = _ref8.id,
      value = _ref8.value,
      cachedItem = _ref8.cachedItem,
      placeholder = _ref8.placeholder,
      markedValue = _ref8.markedValue,
      loading = _ref8.loading,
      items = _ref8.items,
      filteredItems = _ref8.filteredItems,
      isOpen = _ref8.isOpen,
      above = _ref8.above,
      right = _ref8.right,
      typeahead = _ref8.typeahead,
      searchValue = _ref8.searchValue,
      Item = _ref8.Item,
      style = _ref8.style,
      listStyle = _ref8.listStyle,
      setRef = _ref8.setRef,
      disabled = _ref8.disabled,
      isStatic = _ref8.isStatic,
      staticLabel = _ref8.staticLabel,
      multiple = _ref8.multiple,
      tabIndex = _ref8.tabIndex,
      onContainerClick = _ref8.onContainerClick,
      onItemClick = _ref8.onItemClick,
      onSearchChange = _ref8.onSearchChange,
      onContainerKeyDown = _ref8.onContainerKeyDown,
      onContainerKeyUp = _ref8.onContainerKeyUp,
      onSelectAll = _ref8.onSelectAll,
      backEndFilteringEnabled = _ref8.backEndFilteringEnabled;

  var showSelectAll = multiple && value && value.length < items.length / 2;

  return React.createElement(
    'div',
    {
      className: css(styles.container, isStatic && styles.static, disabled && styles.disabled, disabled && isStatic && styles.disabledStatic, style),
      'aria-haspopup': true,
      role: 'button',
      'data-id': 'dropdown-' + id,
      tabIndex: tabIndex,
      ref: setRef('container'),
      onClick: !disabled ? onContainerClick : null,
      onKeyDown: onContainerKeyDown,
      onKeyUp: onContainerKeyUp
    },
    loading ? React.createElement(Spinner, { style: styles.spinner }) : null,
    React.createElement(
      'span',
      {
        className: css(STANDARD_TEXT, styles.label, isStatic && staticLabel && styles.staticLabel)
      },
      isStatic && staticLabel ? staticLabel : getSelectedLabel(value, [].concat(_toConsumableArray(filteredItems || items), [cachedItem]).filter(Boolean), intl),
      !isStatic && !value && placeholder ? React.createElement(
        'span',
        { className: css(styles.placeholder) },
        placeholder
      ) : null
    ),
    React.createElement(Icon, { src: IMG_ICON_CHEVRON_DOWN_SMALL, stroke: 'black' }),
    React.createElement(
      'div',
      {
        className: css(styles.itemListContainer, isStatic && styles.itemListContainerStatic, isOpen && styles.itemListContainerOpen, above && styles.openContainerAbove, right && styles.openContainerRight, listStyle),
        ref: setRef('listContainer')
      },
      typeahead ? React.createElement(
        'div',
        { className: css(styles.typeaheadContainer) },
        React.createElement(SearchInput, {
          style: styles.typeaheadInput,
          placeholder: intl.formatMessage({
            id: 'module.components.dropdown.type.to.search'
          }),
          value: searchValue,
          containerStyle: styles.typeaheadInputContainer,
          inputRef: setRef('searchInput'),
          onChange: onSearchChange
        })
      ) : null,
      React.createElement(
        'div',
        { className: css(styles.itemListScroller) },
        React.createElement(
          'ul',
          {
            className: css(styles.itemList),
            'aria-hidden': !isOpen,
            'aria-label': 'Submenu'
          },
          (filteredItems || items).map(function (_ref9) {
            var itemValue = _ref9.value,
                label = _ref9.label,
                identifier = _ref9.identifier;
            return React.createElement(
              'li',
              {
                className: 'dropdown-item-list__item ' + css(styles.listItemContainer),
                key: itemValue + '-' + label
              },
              React.createElement(
                Item,
                {
                  value: itemValue,
                  id: id,
                  label: label,
                  identifier: identifier,
                  selected: !multiple && itemValue !== null && itemValue !== undefined && isValueSelected(itemValue, value),
                  marked: markedValue && isValueSelected(itemValue, markedValue),
                  onClick: onItemClick
                },
                multiple ? React.createElement(Checkbox, {
                  checked: itemValue && isValueSelected(itemValue, value),
                  style: styles.checkbox
                }) : null
              )
            );
          })
        )
      ),
      multiple ? React.createElement(
        'div',
        {
          className: css(styles.selectAllContainer, STANDARD_TEXT),
          role: 'button',
          tabIndex: 0,
          onClick: function onClick() {
            return onSelectAll(showSelectAll);
          }
        },
        React.createElement(FormattedMessage, {
          id: showSelectAll ? 'module.components.dropdown.select.all' : 'module.components.dropdown.deselect.all'
        }),
        React.createElement(
          'div',
          { className: css(styles.selectAllCheckmark) },
          !showSelectAll ? React.createElement(Icon, { src: IMG_ICON_CHECKMARK, color: TSS_GREEN(0.8) }) : null
        )
      ) : null
    )
  );
};

Dropdown.propTypes = {
  intl: intlShape.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([].concat(dropdownValueProps, [PropTypes.arrayOf(PropTypes.oneOfType(dropdownValueProps))])),
  placeholder: PropTypes.string,
  markedValue: PropTypes.oneOfType([].concat(dropdownValueProps, [PropTypes.arrayOf(PropTypes.oneOfType(dropdownValueProps))])),
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([].concat(dropdownValueProps, [PropTypes.shape({
      format: PropTypes.string,
      pagination: PropTypes.bool
    })])),
    label: PropTypes.string
  })),
  cachedItem: PropTypes.shape({
    value: PropTypes.oneOfType([].concat(dropdownValueProps, [PropTypes.shape({
      format: PropTypes.string,
      pagination: PropTypes.bool
    })])),
    label: PropTypes.string
  }),
  filteredItems: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType(dropdownValueProps),
    label: PropTypes.string
  })),
  isOpen: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  isStatic: PropTypes.bool,
  staticLabel: PropTypes.string,
  style: stylePropType,
  listStyle: stylePropType,
  typeahead: PropTypes.bool,
  backEndFilteringEnabled: PropTypes.bool,
  above: PropTypes.bool,
  right: PropTypes.bool,
  searchValue: PropTypes.string.isRequired,
  sorter: PropTypes.func, // eslint-disable-line react/no-unused-prop-types
  Item: PropTypes.func,
  setRef: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  tabIndex: PropTypes.number,
  onItemClick: PropTypes.func.isRequired,
  onContainerClick: PropTypes.func.isRequired,
  onContainerKeyDown: PropTypes.func.isRequired,
  onContainerKeyUp: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  onSearchChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired
};

Dropdown.defaultProps = {
  id: null,
  value: null,
  placeholder: null,
  backEndFilteringEnabled: false,
  markedValue: null,
  style: null,
  filteredItems: null,
  listStyle: null,
  items: [],
  cachedItem: null,
  typeahead: true,
  above: false,
  right: false,
  disabled: false,
  sorter: defaultSorter,
  multiple: false,
  isStatic: false,
  staticLabel: null,
  loading: false,
  tabIndex: 0,
  Item: enhanceDropdownItem(DropdownItem),
  allowReSelect: true
};

styles = StyleSheet.create({
  container: {
    minWidth: '23rem',
    height: '4.6rem',
    border: '2px solid ' + TSS_BLUE_1(),
    display: 'flex',
    alignItems: 'center',
    padding: '0 1.4rem',
    position: 'relative',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 0,
    color: TSS_DARK_BLUE()
  },

  disabled: {
    color: TSS_DARK_BLUE(0.5),
    backgroundColor: TSS_BLUE_2(),
    cursor: 'not-allowed'
  },

  static: {
    minWidth: 'auto',
    border: '2px solid transparent',
    backgroundColor: 'transparent'
  },

  placeholder: {
    color: TSS_DARK_BLUE(0.5)
  },

  disabledStatic: {
    backgroundColor: 'transparent',
    opacity: 0.5,
    color: TSS_DARK_BLUE()
  },

  spinner: {
    position: 'absolute',
    left: '1.6rem',
    top: '50%',
    marginTop: '-1rem'
  },

  label: {
    flex: '1 1 0%',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    margin: '0 1rem 0 0'
  },

  staticLabel: {
    flex: 'auto',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: TSS_DARK_BLUE(),
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'nowrap'
  },

  itemListContainer: {
    position: 'absolute',
    zIndex: 20,
    top: '100%',
    left: -2,
    minWidth: 'calc( 100% + 4px )',
    margin: '-1px -2px 0 0',
    display: 'none',
    boxShadow: '0 2px 4px 0 rgba( 0, 0, 0, 0.3 )',
    backgroundColor: 'white'
  },

  itemListContainerStatic: {
    margin: '6px -2px 0 0'
  },

  itemListContainerOpen: {
    display: 'block'
  },

  itemListScroller: {
    maxHeight: '40rem',
    overflowY: 'auto'
  },

  itemList: {
    width: '100%',
    listStyleType: 'none',
    padding: 0,
    margin: 0
  },

  listItemContainer: {
    backgroundColor: 'white'
  },

  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '1.2rem 1.4rem',
    outline: 0,

    ':hover': {
      backgroundColor: TSS_BLUE_1(0.3)
    }
  },

  selectedListItem: {
    backgroundColor: TSS_BLUE()
  },

  markedListItem: {
    backgroundColor: TSS_BLUE(0.2)
  },

  checkbox: {
    margin: '0 0 0 0.6rem'
  },

  typeaheadContainer: {
    padding: '0.6rem',
    borderBottom: '2px solid ' + TSS_DARK_BLUE(0.2),
    backgroundColor: 'white'
  },

  typeaheadInput: {
    fontSize: '1.4rem'
  },

  typeaheadInputContainer: {
    height: '3.8rem'
  },

  selectAllContainer: {
    borderTop: '1px solid ' + TSS_DARK_BLUE(0.2),
    height: '3.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1.4rem',
    backgroundColor: 'white',
    boxShadow: '0 -2px 4px 0 rgba( 0, 0, 0, 0.3 )',
    zIndex: 1,
    position: 'relative',
    opacity: 0.7,

    ':hover': {
      opacity: 1
    }
  },

  selectAllCheckmark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: TSS_DARK_BLUE(0.05),
    border: 0,
    borderRadius: 4,
    width: '2.2rem',
    height: '2.2rem'
  },

  openContainerAbove: {
    top: 'initial',
    bottom: '100%',
    marginBottom: 1
  },

  openContainerRight: {
    left: 'auto',
    right: -2
  }
});

var EnhancedDropdown = injectIntl(withRefs(enhanceDropdown(clickOutsideEnhancer(Dropdown, 'onClickOutside'))));

export { EnhancedDropdown as Dropdown, enhanceDropdownItem, dropdownValueProps };